// @ts-nocheck
// Exact live version restored from the production sourcemap (portal.rebels.io, 2026-10-23).
import { jsx as _jsx, jsxs as _jsxs } from 'react/jsx-runtime';
import { Box, CircularProgress, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Add, ClipboardExport } from 'iconsax-react';
import { useMemo, useState } from 'react';
import { black300, black500 } from '../../../../constants/color';
import { CustomChip } from '../../../../components/common/chip/Chip';
import { PrimaryGrid } from '../../../../components/common/grid/PrimaryGrid';
import { PrimaryButon } from '@ui/components/common/button/PrimaryButon';
import { EditHour } from '../dialog/EditHours';
import { HoursStatus } from '@application/common/enums/hours-status';
import { formatDate } from '@infrastructure/helpers';
import { createHourEntryId } from '@application/features/HoursRegistration/hour-write-safety';
const EMPTY_ROWS = [];
export default function HoursTable({ openDialog, week, context, availableDates, loading = false, onSaved }) {
    const [isHourEdit, setIsHourEdit] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xs'));
    const isSm = useMediaQuery(theme.breakpoints.between('sm', 1920));
    const isMdUp = useMediaQuery(theme.breakpoints.up(1920));
    const height = isXs ? 300 : isSm ? 300 : isMdUp ? 500 : 'auto';
    const rawRows = week?.rows ?? EMPTY_ROWS;
    const rows = useMemo(() => rawRows.map((row) => ({
        ...row,
        dateLabel: formatDate(row.date),
        registrationLabel: row.displayName || row.client || '—',
        isPersisted: Boolean(row.assignmentReasonId)
    })), [rawRows]);
    const totalHours = useMemo(() => {
        const totals = new Map();
        rawRows.forEach((row) => {
            if (!totals.has(row.date))
                totals.set(row.date, row.totalHoursInADay);
        });
        return Array.from(totals.values()).reduce((sum, hours) => sum + hours, 0);
    }, [rawRows]);
    const openEdit = (row, addEntry = false) => {
        if (!week || !availableDates.includes(row.date.slice(0, 10)))
            return;
        setSelectedRow({
            id: addEntry ? createHourEntryId() : row.id,
            date: row.date,
            client: addEntry ? null : row.client,
            assignmentReasonId: addEntry ? null : row.assignmentReasonId,
            displayName: addEntry ? null : row.displayName,
            systemCode: addEntry ? null : row.systemCode,
            hours: addEntry ? 0 : row.hours,
            totalHoursInADay: row.totalHoursInADay,
            dailyStatus: row.dailyStatus,
            weekNo: week.weekNo,
            isPersisted: !addEntry && row.isPersisted
        });
        setIsHourEdit(true);
    };
    const columns = [
        { field: 'dateLabel', headerName: 'Date', flex: 1.5, minWidth: 145 },
        { field: 'registrationLabel', headerName: 'Registration', flex: 1.2, minWidth: 130 },
        { field: 'hours', headerName: 'Register', flex: 0.6, minWidth: 80 },
        {
            field: 'dailyStatus',
            headerName: 'Status',
            flex: 0.7,
            minWidth: 100,
            renderCell: (params) => _jsx(CustomChip, { value: HoursStatus[params.row.dailyStatus] })
        },
        {
            field: 'edit',
            headerName: 'Edit',
            headerAlign: 'center',
            align: 'center',
            flex: 0.5,
            minWidth: 110,
            renderCell: (params) => _jsxs(Box, {
                display: 'flex',
                children: [
                    _jsx(IconButton, {
                        'aria-label': `Edit hours for ${params.row.dateLabel}`,
                        onClick: () => openEdit(params.row),
                        disabled: params.row.dailyStatus === HoursStatus.Pending ||
                            params.row.dailyStatus === HoursStatus.Approved ||
                            !availableDates.includes(params.row.date.slice(0, 10)) ||
                            !context,
                        children: _jsx(ClipboardExport, {})
                    }),
                    hasUnavailableHistory &&
                        params.row.isPersisted &&
                        availableDates.includes(params.row.date.slice(0, 10)) &&
                        _jsx(IconButton, {
                            'aria-label': `Add another entry for ${params.row.dateLabel}`,
                            title: 'Add another hour type',
                            onClick: () => openEdit(params.row, true),
                            disabled: isLocked || !context || params.row.totalHoursInADay >= 17,
                            children: _jsx(Add, {})
                        })
                ]
            })
        }
    ];
    if (loading) {
        return _jsx(Box, {
            sx: { minHeight: 260, bgcolor: '#FFFFFF', display: 'grid', placeItems: 'center', borderRadius: '0 0 25px 25px' },
            children: _jsx(CircularProgress, { size: 28 })
        });
    }
    if (!week || rows.length === 0) {
        return _jsx(Box, {
            sx: { minHeight: 260, bgcolor: '#FFFFFF', display: 'grid', placeItems: 'center', borderRadius: '0 0 25px 25px', p: 3 },
            children: _jsx(Typography, {
                variant: 'subtitle2',
                color: black300,
                textAlign: 'center',
                children: 'Select an available context and week to write hours.'
            })
        });
    }
    const isLocked = rows.some((row) => row.dailyStatus === HoursStatus.Pending || row.dailyStatus === HoursStatus.Approved);
    const hasUnavailableHistory = rows.some((row) => !availableDates.includes(row.date.slice(0, 10)));
    return _jsxs(Box, {
        sx: { bgcolor: '#FFFFFF', borderRadius: '0 0 25px 25px', overflow: 'hidden' },
        children: [
            _jsx(PrimaryGrid, { columns: columns, h: height, rows: rows }),
            _jsxs(Grid, {
                container: true,
                px: 2,
                py: 4,
                spacing: 2,
                alignItems: 'center',
                children: [
                    _jsx(Grid, {
                        item: true,
                        md: 7,
                        xs: 5,
                        children: _jsx(Typography, { variant: 'subtitle2', color: black500, children: 'Total hours' })
                    }),
                    _jsx(Grid, {
                        item: true,
                        md: 2,
                        xs: 2,
                        children: _jsx(Typography, { variant: 'body2', color: '#49402A', children: totalHours })
                    }),
                    _jsx(Grid, {
                        item: true,
                        md: 3,
                        xs: 12,
                        sx: { display: 'flex', justifyContent: { xs: 'stretch', md: 'end' } },
                        children: _jsx(Box, {
                            width: { xs: '100%', md: 190 },
                            children: !isLocked &&
                                !hasUnavailableHistory &&
                                _jsx(PrimaryButon, { onClick: openDialog, children: 'Fill in weekly hours' })
                        })
                    }),
                    hasUnavailableHistory &&
                        _jsx(Grid, {
                            item: true,
                            xs: 12,
                            children: _jsx(Typography, {
                                variant: 'body2',
                                color: black300,
                                children: 'Saved hours on unavailable dates are read-only. Use Edit or Add on an available date to register hours.'
                            })
                        })
                ]
            }),
            _jsx(EditHour, {
                open: isHourEdit,
                handleClose: () => setIsHourEdit(false),
                selectedRow: selectedRow,
                week: week,
                context: context,
                allDailyHoursOfWeek: rawRows,
                onSaved: onSaved
            })
        ]
    });
}
